import { useMemo, useState } from 'react'
import { useClickOutside } from '@mantine/hooks'
import { formatHumanName } from '@medplum/core'
import { IconCheck, IconSearch } from '@tabler/icons-react'
import { useActivePractitioner } from '@/pages/Schedule/context/'
import { PractitionerWithColor, DOCTOR_COLORS } from './types'

export const PractitionerMultiSelect = ({
  practitionerOptions,
}: {
  practitionerOptions: PractitionerWithColor[]
}) => {
  const {
    selectedMultiplePractitionerIds: selectedPractitioners,
    handleSetSelectedMultiplePractitionerIds: setSelectedPractitioners,
  } = useActivePractitioner()
  const [opened, setOpened] = useState(false)
  const [query, setQuery] = useState('')
  const panelRef = useClickOutside(() => setOpened(false))

  const options = useMemo(
    () =>
      practitionerOptions.map((practitioner, index) => ({
        id: practitioner.id as string,
        name: practitioner.name?.[0] ? formatHumanName(practitioner.name[0]) : '',
        color: practitioner.color || DOCTOR_COLORS[index % DOCTOR_COLORS.length],
      })),
    [practitionerOptions],
  )

  const filteredOptions = useMemo(() => {
    const text = query.trim().toLowerCase()
    if (!text) return options
    return options.filter((option) => option.name.toLowerCase().includes(text))
  }, [options, query])

  const togglePractitioner = (id: string) => {
    if (selectedPractitioners.includes(id)) {
      setSelectedPractitioners(selectedPractitioners.filter((selectedId) => selectedId !== id))
    } else {
      setSelectedPractitioners([...selectedPractitioners, id])
    }
  }

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpened((o) => !o)}
        className="schedule-v2-mulish inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md border border-[#D8DBE0] bg-white px-2.5 py-[3px] !text-[16px] !font-semibold text-[#33383F] transition hover:bg-[#F4F6F8]"
      >
        Select doctor
        {selectedPractitioners.length > 0 && (
          <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#E7ECF2] px-1.5 text-[11.5px] font-bold text-[#4A5160]">
            {selectedPractitioners.length}
          </span>
        )}
        <span
          className="text-[11px] transition-transform duration-150"
          style={{ transform: opened ? 'rotate(180deg)' : 'none' }}
        >
          ▾
        </span>
      </button>

      {opened && (
        <div className="absolute top-[calc(100%+8px)] left-0 z-60 w-[278px] rounded-[13px] border border-[#E4E7EB] bg-white p-2 shadow-[0_16px_40px_rgba(16,24,40,.2)]">
          <div className="mx-0.5 mt-0.5 mb-2 flex items-center gap-2 rounded-[9px] border border-[#E2E4E8] px-[11px] py-2">
            <IconSearch size={16} stroke={2} className="shrink-0 text-[#9AA0A8]" />
            <input
              placeholder="Search doctors…"
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              className="w-full border-none bg-transparent text-[13.5px] text-[#1F2733] outline-none"
            />
          </div>
          <div className="max-h-[280px] overflow-y-auto">
            {filteredOptions.map((option) => {
              const isSelected = selectedPractitioners.includes(option.id)
              return (
                <div
                  key={option.id}
                  onClick={() => togglePractitioner(option.id)}
                  className="flex cursor-pointer items-center gap-2.5 rounded-[9px] px-[11px] py-[9px]"
                  style={{ background: isSelected ? '#F0F6FF' : 'transparent' }}
                >
                  <span
                    className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] text-white"
                    style={{
                      border: isSelected ? 'none' : '1.5px solid #C4C9D0',
                      background: isSelected ? option.color : '#fff',
                    }}
                  >
                    {isSelected && <IconCheck size={12} stroke={3} />}
                  </span>
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ background: option.color }}
                  />
                  <span className="flex-1 text-[13.5px] font-semibold text-[#1F2733]">
                    {option.name}
                  </span>
                </div>
              )
            })}
            {filteredOptions.length === 0 && (
              <p className="px-[11px] py-2 text-[13px] text-[#9AA0A8]">No doctors found</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
